import { React, useEffect, useState, useCallback, useMemo } from 'react';
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { motion } from 'framer-motion';
import "./index.scss";

import SideModal from '../../components/ui/SideModal';
import Pagination from '../../components/ui/Pagination';
import NoDataFound from "../../components/ui/NoDataFound";
import DropDown from "../../components/ui/DropDown";
import { toaster } from "../../components/ui/Toaster";
import SkeletonLoader from "../../components/ui/SkeletonLoader/index.js";

import SideNav from "../../components/PostLogin/SideNav";
import OrganizationTileView from '../../components/PostLogin/Organization/OrganizationTileView';
import OrganizationListView from "../../components/PostLogin/Organization/OrganizationListView";
import AddOrganizationModal from '../../components/PostLogin/Organization/AddOrganizationModel';

import { formatNumber, getloggedInUserType } from "../../utils/util.js";
import { tileViewIcon, listViewIcon, searchIcon } from "../../utils/icons.js";

import { useSelector, useDispatch } from 'react-redux';
import { getOrganizationStats, getOrganizationList, resetOrganizationListResponse, resetAddOrganizationResponse } from '../../store/actions/organizationActions.js';
import { getCustomersList, resetCustomersList } from '../../store/actions/customerAction.js';

const Organization = () => {

    const organizationStatsResponse = useSelector((state) => state.organization?.organizationStatsResponse);
    const organizationListResponse = useSelector((state) => state.organization?.organizationList);
    const deleteOrganizationResponse = useSelector((state)=> state.organization?.deleteOrganizationResponse);
    const customersListResponse = useSelector((state) => state.customer?.customersListResponse);

    const addOrganizationResponse = useSelector((state) => state.organization?.addOrganizationResponse);

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const [viewType, setViewType] = useState('tile');
    const [showAddOrgSidePanel, setShowAddOrgSidePanel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isStatsLoading, setIsStatsLoading] = useState(false);

    const [searchValue, setSearchValue] = useState('');
    const [updatedSearchValue, setUpdatedSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);

    const [selectedCustomer, setSelectedCustomer] = useState({});
    const [customersList, setCustomersList] = useState([]);
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);    
    const [customerListLoading, setCustomerListLoading] = useState(false);
    const [customerListCurrentPage, setCustomerListCurrentPage] = useState(1);
    const [customerListPageLoading, setCustomerListPageLoading] = useState(false);
    const [customerListTotalPage, setCustomerListTotalPage] = useState(0);

    const [organizationList, setOrganizationList] = useState(null)

    const closeAddOrgSidePanel = () => {
        setShowAddOrgSidePanel(false)
    }

    const customerChange = (value) => {
        setSelectedCustomer(value);
        setCurrentPage(1);
        setCurrentPageSize(10);
    }
    
    const searchOrganization = (event) => {
        setSearchValue(event.target.value);
        requestSearch(event.target.value)
        setCurrentPage(1);
    }

    const requestSearch = useMemo(() => debounce((searchTerm) => {        
        setUpdatedSearchValue(searchTerm)
    }, 500), []);

    const fetchOrganizations = useCallback(() => {
        const userType = getloggedInUserType();

        if (userType === "internal" && selectedCustomer && Object.keys(selectedCustomer).length > 0) {
            const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}&customer_id=${selectedCustomer?.value}`;
            const queryParams = updatedSearchValue ? `&query=${updatedSearchValue}` : "";
            const url = `${baseUrl}${queryParams}`;
            setIsLoading(true);
            dispatch(getOrganizationList(url));
        } else if (userType !== "internal") {
            const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}`;
            const queryParams = updatedSearchValue ? `&query=${updatedSearchValue}` : "";
            const url = `${baseUrl}${queryParams}`;
            setIsLoading(true);
            dispatch(getOrganizationList(url));
        }
    }, [updatedSearchValue, currentPage, currentPageSize, dispatch, selectedCustomer]);

    const fetchCustomersList = useCallback(()=>{
        setCustomerListLoading(true)
        setIsLoading(true);
        const baseUrl = `&page=1&page_size=10`;
        const queryParams = customerSearch ? `&query=${customerSearch}` : "";
        const url = `${baseUrl}${queryParams}`;
        dispatch(getCustomersList(url));
    },[dispatch, customerSearch])

    const fetchOrganizationStats = useCallback(()=>{
        setIsStatsLoading(true);
        dispatch(getOrganizationStats())
    },[dispatch])
    

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const backToMainPage = () => {
        if(addOrganizationResponse?.detail?.status === "success") {
            setIsLoading(true)
            setCurrentPage(1);
            setCurrentPageSize(10);
            fetchOrganizations()
        }
        dispatch(resetAddOrganizationResponse())
        closeAddOrgSidePanel()
    }

    useEffect(()=>{        
        const baseUrl = `&page=${customerListCurrentPage}&page_size=10`
        const queryParams = customerSearch ? `&query=${customerSearch}` : "";
        const url = `${baseUrl}${queryParams}`
        dispatch(getCustomersList(url));
    },[customerSearch, dispatch, customerListCurrentPage])

    useEffect(() => {
        const userType = getloggedInUserType();
        if (userType === "internal" && selectedCustomer && Object.keys(selectedCustomer).length === 0) {
            fetchCustomersList();
        } else if (userType !== "internal") {
            fetchOrganizations();
        }
    }, [fetchOrganizations, selectedCustomer, fetchCustomersList]);

    useEffect(() => {
        if(organizationListResponse!= null) {
            setIsLoading(false)
            setOrganizationList(organizationListResponse)
            dispatch(resetOrganizationListResponse())
        }
    }, [organizationListResponse, dispatch]);

    useEffect(() => {
        if(deleteOrganizationResponse !== null) {
            if (deleteOrganizationResponse?.detail?.status === "success") {
                toaster.addToast(deleteOrganizationResponse?.detail?.description, "success");
                fetchOrganizations();
            } else {
                toaster.addToast(deleteOrganizationResponse?.description, "error");
            }
        }
    }, [deleteOrganizationResponse, fetchOrganizations]);

    useEffect(() => {
        if (customersListResponse != null) {
            setCustomerListLoading(false)
            setCustomerSearchLoading(false)
            setCustomerListPageLoading(false)
            const list = customersListResponse.results.map((data) => ({
                id: data.customer_id,
                name: data.name,
                value: data.customer_id,
                logo: data.logo,
                label: data.name
            }));
            setCustomersList((prevList) => {
                const uniqueList = [...prevList, ...list].reduce((acc, item) => {
                    if (!acc.some(existingItem => existingItem.id === item.id)) {
                        acc.push(item);
                    }
                    return acc;
                }, []);
                return uniqueList; //Prevents duplicate additions, avoiding redundant state updates
            });
            setCustomerListTotalPage(customersListResponse?.meta?.total_pages)
            if(customerSearch === "" && Object.keys(selectedCustomer).length === 0) {
                if(list.length > 0) {
                    setSelectedCustomer(list[0]);
                } else {
                    setSelectedCustomer("")
                }
            }
            dispatch(resetCustomersList())
        }
    }, [customersListResponse, customerSearch, selectedCustomer, dispatch]);

    useEffect(() => {
        if (selectedCustomer && Object.keys(selectedCustomer).length > 0) {
            fetchOrganizations();
        }
        if(getloggedInUserType() === "customer") {
            fetchOrganizationStats()
        }        
    }, [selectedCustomer, fetchOrganizations, fetchOrganizationStats]);

    useEffect(() => {
        if(organizationStatsResponse !== null) {
            setIsStatsLoading(false)
        }
    },[organizationStatsResponse])

   
    return (
        <div className="organization-container">
            <SideNav />
            <div className="organization-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left">
                        <h1>{t("organizationPage.heading")}</h1>
                    </div>
                    <div className="header-right">
                        {getloggedInUserType() === "internal" && <button onClick={()=>{setShowAddOrgSidePanel(true)}} disabled={Object.keys(selectedCustomer).length === 0}><span>&#43;</span>{t("organizationPage.addOrganization")}</button>}
                        {getloggedInUserType() !== "internal" && <button onClick={()=>{setShowAddOrgSidePanel(true)}}><span>&#43;</span>{t("organizationPage.addOrganization")}</button>}
                    </div>
                </div>
                <div className="page-search-header px-4 py-3  d-flex align-items-center justify-content-between">
                    <div className="left-search-container d-flex align-items-center gap-4 organization-page-customer-list-dropdown">
                                             
                        {getloggedInUserType() === "internal" &&
                            <>
                                <div className="customer-view">
                                    <p>{t('organizationPage.selectCustomer')}</p>
                                    {
                                        customerListLoading ?
                                        <SkeletonLoader type={"line"} count={1} currentPage={"organizationPage"} />
                                        :
                                        <DropDown
                                            options={customersList}
                                            placeHolder={"Select Customer"}
                                            onChange={customerChange}
                                            isSearchable= {true}
                                            isMulti={false}
                                            dropDownType = {'scanInfo'}
                                            imageType={"customer"}
                                            defaultSelectedValue={selectedCustomer?.value || ""}
                                            searchType={"server"}
                                            updateSearchValue={(data)=>{
                                                setCustomersList([])
                                                setCustomerSearchLoading(true)
                                                setCustomerSearch(data)
                                                setCustomerListCurrentPage((prevPage) => (prevPage !== 1 ? 1 : prevPage)); 
                                            }}
                                            isSearchLoading={customerSearchLoading}
                                            onScrollEnd = {()=>{
                                                if(!customerListPageLoading && (customerListTotalPage > customerListCurrentPage)) {
                                                    setCustomerListPageLoading(true)
                                                    setCustomerListCurrentPage((prevPage) => prevPage + 1)
                                                }
                                            }}
                                            pageLoading={customerListPageLoading}
                                        />
                                    }
                                    
                                </div>
                                <p className="border-right"></p>
                            </>
                            
                        }
                        <div className="search-container position-relative">
                            <span className="position-absolute search-icon">{searchIcon()}</span>
                            <input type='text' placeholder='Search Organization' className="search-input" value={searchValue} onChange={searchOrganization}/>
                        </div>
                    </div>
                    <div className="right-view-option d-flex">
                        <div className={`tile-view-image ${viewType === 'tile' ? 'active' : ''}`} onClick={()=>setViewType('tile')}>
                            {tileViewIcon()}
                        </div>
                        <div className={`list-view-image ${viewType === 'list' ? 'active' : ''}`} onClick={()=>setViewType('list')}>
                            {listViewIcon()}
                        </div>
                    </div>
                </div>
                <div className="page-main-section p-4">
                    {getloggedInUserType() === "customer" && <div className="total-domain-scans d-flex mt-3 rounded">                        
                        <div className="total-domains-container rounded p-3">
                            {
                                isStatsLoading ?
                                <SkeletonLoader type={"line"} count={2} currentPage={"organization"}/>
                                :
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <div className='domain-icon'>
                                        <h4 className="m-0 mt-1">{formatNumber(organizationStatsResponse?.organizations?.total)}</h4>
                                        <div className="user-count mx-3 mt-2">
                                            <span className='active-icon d-inline-block'></span>
                                            <span className='passive-icon d-inline-block'></span>
                                            <p className='total-scans-text'>{t('organizationPage.totalOrganization')}</p>
                                        </div>
                                    </div>
                                    <div className="p-wrapper d-flex justify-content-between gap-3">
                                        <p className='mt-2'><span className='count d-inline-block'>{formatNumber(organizationStatsResponse?.organizations?.internal)}</span>{t('organizationPage.internal')}</p>
                                        <p className='mt-2'><span className='count-passive'>{formatNumber(organizationStatsResponse?.organizations?.portfolio)}</span>{t('organizationPage.portfolio')}</p>
                                    </div>
                                </motion.div>
                            }                            
                        </div> 
                        <div className="total-domains-container rounded p-3 margin-right">
                            {
                                isStatsLoading ?
                                <SkeletonLoader type={"line"} count={2} currentPage={"organization"}/>
                                :
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                >
                                    <h4 className="m-0 mt-1">{isStatsLoading ? "...." : organizationStatsResponse?.organizations?.third_party}</h4>
                                    <p className='mt-2'>{t('organizationPage.thirdParty')}</p>
                                </motion.div>
                            }                            
                        </div>                       
                    </div>}
                    <h2 className="heading">{t("organizationPage.pageHeading")}</h2>
                    {viewType === 'tile' && <OrganizationTileView organizationList={organizationList} selectedCustomer={selectedCustomer} isLoading={isLoading} />}
                    {viewType === 'list' && <OrganizationListView organizationList={organizationList} selectedCustomer={selectedCustomer} isLoading={isLoading} />}
                    {!isLoading && organizationList != null && organizationList?.results.length > 0 && <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        totalCount={organizationList.meta.total_results}
                        onPageChange={setCurrentPageandRecordSize}
                    />}
                    {
                        !isLoading && organizationList?.meta?.total_results === 0 && 
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <NoDataFound />
                        </motion.div>
                    }                    
                </div>
            </div>
            <SideModal showSidePanel={showAddOrgSidePanel} hideSidePanel={backToMainPage}>
                {showAddOrgSidePanel && <AddOrganizationModal backToMainPage={backToMainPage} selectedCustomer={selectedCustomer} />}
            </SideModal>
        </div>
    )
}

export default Organization;